import React from 'react';
import { 
  Typography, 
  LinearProgress, 
  Box, 
  Paper, 
  Avatar,
  Chip,
  alpha 
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

const ProgressTracker = ({ completedLessons, totalLessons, testScore }) => {
  const progress = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  const isCompleted = progress === 100;

  // Determinar color según progreso
  const getProgressColor = () => {
    if (progress >= 100) return COLORS.success;
    if (progress >= 50) return COLORS.primary;
    return COLORS.warning;
  };

  // Determinar color del test
  const getTestColor = () => {
    if (testScore === null) return '#9e9e9e';
    if (testScore >= 70) return COLORS.success;
    if (testScore >= 50) return COLORS.warning;
    return COLORS.error;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '2px solid #e0e0e0',
        mb: 4
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <TrendingUpIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
            Progreso del Curso
          </Typography>
        </Box>

        {isCompleted && (
          <Chip
            icon={<EmojiEventsIcon />}
            label="¡Completado!"
            sx={{
              bgcolor: alpha(COLORS.success, 0.15),
              color: COLORS.success,
              fontWeight: 700,
              '& .MuiChip-icon': { color: COLORS.success }
            }}
          />
        )}
      </Box>

      {/* Barra de progreso */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>
            Progreso general
          </Typography>
          <Typography variant="body2" sx={{ color: getProgressColor(), fontWeight: 700 }}>
            {progress.toFixed(0)}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 12, 
            borderRadius: 6,
            bgcolor: alpha(getProgressColor(), 0.15),
            '& .MuiLinearProgress-bar': {
              borderRadius: 6,
              background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
            }
          }} 
        />
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Lecciones */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 150,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(COLORS.primary, 0.08),
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: alpha(COLORS.primary, 0.15),
              color: COLORS.primary
            }}
          >
            <MenuBookIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ color: '#636e72' }}>
              Lecciones
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
              {completedLessons} / {totalLessons}
            </Typography>
          </Box>
        </Paper>

        {/* Test */}
        {testScore !== null && (
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 150,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(getTestColor(), 0.08),
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: alpha(getTestColor(), 0.15),
                color: getTestColor()
              }}
            >
              <QuizIcon />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ color: '#636e72' }}>
                Test Final
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getTestColor() }}>
                  {testScore}%
                </Typography>
                {testScore >= 70 && (
                  <CheckCircleIcon sx={{ fontSize: 18, color: COLORS.success }} />
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Paper>
  );
};

export default ProgressTracker;